import { ref } from 'vue';
import { supabase, supabaseAdmin } from '@/services/supabase';
import { logger } from '@/services/logger';

/**
 * Sanitize a filename to ensure it's compatible with Supabase Storage
 * Uses URL encoding to handle special characters and non-ASCII characters
 */
function sanitizeFileName(fileName: string): string {
  // Split the filename into name and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  let name = fileName;
  let extension = '';
  
  if (lastDotIndex !== -1) {
    name = fileName.substring(0, lastDotIndex);
    extension = fileName.substring(lastDotIndex);
  }
  
  // URL encode the name part (but keep the extension as is)
  let encodedName = encodeURIComponent(name);
  
  // Replace certain characters that might still cause issues
  encodedName = encodedName.replace(/%/g, '_');
  
  // Combine encoded name with the original extension
  let sanitized = encodedName + extension;
  
  // Ensure the filename is not too long (max 255 characters)
  if (sanitized.length > 255) {
    // If too long, truncate the name part, not the extension
    const maxNameLength = 255 - extension.length;
    sanitized = encodedName.substring(0, maxNameLength) + extension;
  }
  
  return sanitized;
}

export function useStorage() {
  const loading = ref(false);
  const error = ref<Error | null>(null);

  /**
   * Helper function to extract bucket and path from Supabase URL
   */
  function extractStorageInfoFromUrl(url: string) {
    if (!url) return null;

    try {
      // Parse the URL to extract bucket and path
      // Example URL: https://extravagant.ai-core.dev.azcld.advisori.de/storage/v1/object/public/Templates/1746703577729-1746616064363-EMPTY_2025_responsible_20planners_20report_COM_template_20(3).xlsx

      const regex = /\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)/;
      const matches = url.match(regex);

      if (matches && matches.length >= 3) {
        return {
          bucket: matches[1],
          path: decodeURIComponent(matches[2])
        };
      }

      return null;
    } catch (error) {
      logger.error('Error parsing storage URL', { url, error });
      return null;
    }
  }

  /**
   * Save file information to the database
   */
  async function saveFileToDatabase(
    filePath: string,
    fileName: string,
    bucket: string,
    fileSize?: number,
    onlyPcoAccess?: boolean,
    documentStatus: string = 'CREATING',
    userId?: string,
    forecastId: string = '',
    reportType: string = 'COM',
    createDocumentEntry: boolean = true
  ) {
    try {
      // Get the public URL for the file
      const publicUrl = getPublicUrl(bucket, filePath);
      
      // Calculate file size in KB if provided
      const sizeInKB = fileSize ? Math.round(fileSize / 1024) : undefined;
      
      // Insert into the files table
      const { data, error: dbError } = await supabase
        .from('files')
        .insert({
          name: fileName,
          url: publicUrl,
          size: sizeInKB, // Store file size in KB
          only_pco_access: onlyPcoAccess // Set based on active tab
        })
        .select()
        .single();
      
      if (dbError) {
        throw dbError;
      }
      
      logger.info('File information saved to database', {
        fileId: data.id,
        fileName: data.name,
        only_pco_access: data.only_pco_access
      });
      
      // Create entry in documents table only if createDocumentEntry is true
      if (createDocumentEntry) {
        const { data: documentData, error: documentError } = await supabase
          .from('documents')
          .insert({
            name: fileName,
            status: documentStatus,
            created_by: userId,
            forecast_id: forecastId,
            file_id: data.id,
            report_type: reportType
          })
          .select()
          .single();

        if (documentError) {
          logger.error('Error creating document entry', { error: documentError });
          // We don't throw here to avoid failing the whole operation if document creation fails
        } else {
          logger.info('Document entry created', {
            documentId: documentData.id,
            fileId: data.id
          });
        }
      } else {
        logger.info('Skipping document entry creation as per request', {
          fileId: data.id
        });
      }

      return data;
    } catch (err) {
      logger.error('Error saving file information to database', { error: err });
      throw err;
    }
  }

  /**
   * Upload a file to Supabase Storage and save to database
   */
  async function uploadFile(bucket: string, path: string, file: File, options?: {
    onlyPcoAccess?: boolean;
    customFileName?: string;
    documentStatus?: string;
    userId?: string;
    forecastId?: string;
    reportType?: string;
    createDocumentEntry?: boolean;
  }) {
    loading.value = true;
    
    try {
      // Sanitize the file path to prevent invalid character errors
      const sanitizedPath = sanitizeFileName(path);
      
      if (sanitizedPath !== path) {
        logger.info('File path sanitized', { original: path, sanitized: sanitizedPath });
      }
      
      // Try to use the admin client first to bypass RLS policies
      if (supabaseAdmin) {
        try {
          logger.info('Using admin client for file upload to bypass RLS');
          
          const { data, error: adminErr } = await supabaseAdmin.storage
            .from(bucket)
            .upload(sanitizedPath, file);
          
          if (!adminErr) {
            // Save to database
            const finalFileName = options?.customFileName || file.name;
            
            await saveFileToDatabase(
              sanitizedPath,
              finalFileName,
              bucket,
              file.size,
              options?.onlyPcoAccess,
              options?.documentStatus,
              options?.userId,
              options?.forecastId,
              options?.reportType,
              options?.createDocumentEntry,
            );
            
            return {
              ...data,
              path: sanitizedPath // Return the sanitized path for reference
            };
          }
          
          // If admin client fails, log the error but continue with regular client
          logger.warn('Admin client upload failed, falling back to regular client', { error: adminErr });
        } catch (adminErr) {
          logger.warn('Error using admin client for upload', { error: adminErr });
          // Continue with regular client
        }
      }
      
      // Use regular client as fallback
      const { data, error: err } = await supabase.storage
        .from(bucket)
        .upload(sanitizedPath, file);
      
      if (err) {
        // Handle specific error types
        const errorObj = err as any; // Type assertion to access any properties
        if (errorObj.statusCode === '404' && errorObj.message === 'Bucket not found') {
          const errorMsg = `Bucket "${bucket}" not found. Please create it in the Supabase dashboard.`;
          logger.error(errorMsg, { bucket });
          throw new Error(errorMsg);
        } else if (errorObj.statusCode === '403' && errorObj.message.includes('row-level security policy')) {
          const errorMsg = `Permission denied due to RLS policy. Make sure you have the correct permissions or try using the admin client.`;
          logger.error(errorMsg, { bucket });
          throw new Error(errorMsg);
        }
        throw err;
      }
      
      // Save to database
      const finalFileName = options?.customFileName || file.name;
      
      await saveFileToDatabase(
        sanitizedPath,
        finalFileName,
        bucket,
        file.size,
        options?.onlyPcoAccess,
        options?.documentStatus,
        options?.userId,
        options?.forecastId,
        options?.reportType,
        options?.createDocumentEntry
      );
      
      return {
        ...data,
        path: sanitizedPath // Return the sanitized path for reference
      };
    } catch (err) {
      logger.error('File upload error:', { error: err });
      
      // Create a more user-friendly error message
      let errorMessage = 'File upload failed';
      
      if (typeof err === 'object' && err !== null) {
        const errorObj = err as any;
        if (errorObj.statusCode === '404' && errorObj.message === 'Bucket not found') {
          errorMessage = `Bucket "${bucket}" not found. Please create it in the Supabase dashboard.`;
        } else if (errorObj.message) {
          errorMessage = errorObj.message;
        }
      }
      
      error.value = new Error(errorMessage);
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Download a file from Supabase Storage
   */
  async function downloadFile(bucket: string, path: string) {
    loading.value = true;
    
    try {
      // Try to use the admin client first to bypass RLS policies
      if (supabaseAdmin) {
        try {
          logger.info('Using admin client for file download to bypass RLS');
          
          const { data, error: adminErr } = await supabaseAdmin.storage
            .from(bucket)
            .download(path);
          
          if (!adminErr) {
            return data;
          }
          
          // If admin client fails, log the error but continue with regular client
          logger.warn('Admin client download failed, falling back to regular client', { error: adminErr });
        } catch (adminErr) {
          logger.warn('Error using admin client for download', { error: adminErr });
          // Continue with regular client
        }
      }
      
      // Use regular client as fallback
      const { data, error: err } = await supabase.storage
        .from(bucket)
        .download(path);
      
      if (err) throw err;
      
      return data;
    } catch (err) {
      logger.error('File download error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get a public URL for a file
   */
  function getPublicUrl(bucket: string, path: string) {
    // Try to use the admin client first to bypass RLS policies
    if (supabaseAdmin) {
      try {
        const { data } = supabaseAdmin.storage
          .from(bucket)
          .getPublicUrl(path);
        
        return data.publicUrl;
      } catch (err) {
        logger.warn('Error using admin client for getPublicUrl, falling back to regular client', { error: err });
        // Continue with regular client
      }
    }
    
    // Use regular client as fallback
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    
    return data.publicUrl;
  }

  /**
   * List all files in a bucket/folder
   */
  async function listFiles(bucket: string, path?: string) {
    loading.value = true;
    
    try {
      // First query the files table to get the list of files
      const { data: filesData, error: filesError } = await supabase
        .from('files')
        .select('*')

      if (filesError) {
        throw filesError;
      }

      if (!filesData || filesData.length === 0) {
        return [];
      }

      // Get the file metadata from storage for each file
      const filePromises = filesData.map(async (file) => {
        try {
          // Try to use the admin client first to bypass RLS policies
          if (supabaseAdmin) {
            try {
              const { data: adminData, error: adminErr } = await supabaseAdmin.storage
                .from(bucket)
                .list(path, {
                  search: file.name
                });
              
              if (!adminErr && adminData && adminData.length > 0) {
                return {
                  ...adminData[0],
                  ...file
                };
              }
            } catch (adminErr) {
              logger.warn('Error using admin client for file metadata', { error: adminErr });
            }
          }
          
          // Use regular client as fallback
          const { data, error: err } = await supabase.storage
            .from(bucket)
            .list(path, {
              search: file.name
            });
          
          if (err) {
            logger.warn('Error getting file metadata', { error: err });
            return {
              ...file,
              metadata: {
                size: file.size
              }
            };
          }
          
          if (data && data.length > 0) {
            return {
              ...data[0],
              ...file
            };
          }
          
          return {
            ...file,
            metadata: {
              size: file.size
            }
          };
        } catch (err) {
          logger.warn('Error getting file metadata', { error: err });
          return {
            ...file,
            metadata: {
              size: file.size
            }
          };
        }
      });

      const filesWithMetadata = await Promise.all(filePromises);
      
      return filesWithMetadata;
    } catch (err) {
      logger.error('File listing error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a file from storage and database
   */
  async function deleteFile(bucket: string, fileName: string) {
    loading.value = true;
    
    try {
      // First get the file record from the database
      const { data: fileRecord, error: queryError } = await supabase
        .from('files')
        .select('id, url')
        .eq('name', fileName)
        .single();

      if (queryError) {
        throw queryError;
      }

      if (!fileRecord) {
        throw new Error(`File "${fileName}" not found in database`);
      }

      // Extract the storage path from the URL
      const storagePath = fileRecord.url.split('/').pop();
      if (!storagePath) {
        throw new Error(`Invalid file URL: ${fileRecord.url}`);
      }

      // Try to use the admin client first to bypass RLS policies
      if (supabaseAdmin) {
        try {
          logger.info('Using admin client for file deletion to bypass RLS');
          
          const { error: adminErr } = await supabaseAdmin.storage
            .from(bucket)
            .remove([storagePath]);
          
          if (!adminErr) {
            // Delete the database record
            const { error: deleteError } = await supabaseAdmin
              .from('files')
              .delete()
              .eq('id', fileRecord.id);
            
            if (deleteError) {
              throw deleteError;
            }
            
            return true;
          }
          
          // If admin client fails, log the error but continue with regular client
          logger.warn('Admin client deletion failed, falling back to regular client', { error: adminErr });
        } catch (adminErr) {
          logger.warn('Error using admin client for deletion', { error: adminErr });
          // Continue with regular client
        }
      }
      
      // Use regular client as fallback
      const { error: storageErr } = await supabase.storage
        .from(bucket)
        .remove([storagePath]);
      
      if (storageErr) {
        throw storageErr;
      }

      // Delete the database record
      const { error: deleteError } = await supabase
        .from('files')
        .delete()
        .eq('id', fileRecord.id);
      
      if (deleteError) {
        throw deleteError;
      }
      
      return true;
    } catch (err) {
      logger.error('File deletion error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Download a file by its name from the database
   * This function retrieves the URL from the database and triggers the browser download
   */
  async function downloadFileByName(fileName: string, bucket: string) {
    loading.value = true;
    
    try {
      // Query the files table to get the correct URL
      const { data, error: queryError } = await supabase
        .from('files')
        .select('url')
        .eq('name', fileName)
        .single();
      
      if (queryError) {
        throw queryError;
      }
      
      if (!data || !data.url) {
        throw new Error(`File "${fileName}" not found in database or URL is missing`);
      }
      
      // Use the URL from the database
      const publicUrl = data.url;
      
      logger.info('File URL retrieved from database', {
        bucket,
        fileName,
        url: publicUrl
      });
      
      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = publicUrl;
      link.download = fileName.split('/').pop() || fileName; // Extract the filename from the path
      link.target = '_blank';
      
      // Append to the document, click it, and then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      logger.info('File download initiated', {
        bucket,
        fileName,
        url: publicUrl
      });
      
      return publicUrl;
    } catch (err) {
      logger.error('Failed to download file', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a file by its ID from storage and database
   */
  async function deleteFileById(fileId: string) {
    loading.value = true;
    
    try {
      // First get the file record from the database using the ID
      const { data: fileRecord, error: queryError } = await supabase
        .from('files')
        .select('id, url, bucket_name')
        .eq('id', fileId)
        .single();

      if (queryError) {
        throw queryError;
      }

      if (!fileRecord) {
        throw new Error(`File with ID "${fileId}" not found in database`);
      }

      // Extract the storage path from the URL
      const urlInfo = extractStorageInfoFromUrl(fileRecord.url);
      if (!urlInfo) {
        throw new Error(`Invalid file URL: ${fileRecord.url}`);
      }

      const bucket = fileRecord.bucket_name || urlInfo.bucket;
      const path = urlInfo.path;

      // Try to use the admin client first to bypass RLS policies
      if (supabaseAdmin) {
        try {
          logger.info('Using admin client for file deletion by ID to bypass RLS');
          
          const { error: adminErr } = await supabaseAdmin.storage
            .from(bucket)
            .remove([path]);
          
          if (!adminErr) {
            // Delete the database record
            const { error: deleteError } = await supabaseAdmin
              .from('files')
              .delete()
              .eq('id', fileId);
            
            if (deleteError) {
              throw deleteError;
            }
            
            return true;
          }
          
          // If admin client fails, log the error but continue with regular client
          logger.warn('Admin client deletion failed, falling back to regular client', { error: adminErr });
        } catch (adminErr) {
          logger.warn('Error using admin client for deletion', { error: adminErr });
          // Continue with regular client
        }
      }
      
      // Use regular client as fallback
      const { error: storageErr } = await supabase.storage
        .from(bucket)
        .remove([path]);
      
      if (storageErr) {
        throw storageErr;
      }

      // Delete the database record
      const { error: deleteError } = await supabase
        .from('files')
        .delete()
        .eq('id', fileId);
      
      if (deleteError) {
        throw deleteError;
      }
      
      return true;
    } catch (err) {
      logger.error('File deletion by ID error', { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    error,
    uploadFile,
    downloadFile,
    downloadFileByName,
    getPublicUrl,
    listFiles,
    deleteFile,
    deleteFileById
  };
}
